import { inject, Injectable, Injector } from '@angular/core';
import {
  DBSchema,
  IDBPDatabase,
  IndexNames,
  openDB,
  OpenDBCallbacks,
  StoreKey,
  StoreNames,
  StoreValue,
} from 'idb';

export function injectIndexedDb<S extends DBSchema>(
  dbName: string,
  name: StoreNames<S>,
  schemaVersion = 1,
  callback?: OpenDBCallbacks<S>,
) {
  const injector = inject(Injector);
  const db = injector.get(IndexedDbService<S>);
  db.init(dbName, name, schemaVersion, callback);

  return db;
}

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService<S extends DBSchema> {
  private dbPromise: Promise<IDBPDatabase<S>> | null = null;
  private storeName: StoreNames<S> = '' as StoreNames<S>;

  public init(
    dbName: string,
    name: StoreNames<S>,
    schemaVersion = 1,
    callback?: OpenDBCallbacks<S>,
  ) {
    this.storeName = name;
    this.dbPromise = openDB<S>(dbName, schemaVersion, callback);
  }

  public async add(item: StoreValue<S, StoreNames<S>>) {
    const db = await this.dbPromise;
    return db!.add(this.storeName, item);
  }

  public async get(key: StoreKey<S, StoreNames<S>> | IDBKeyRange) {
    const db = await this.dbPromise;
    return db!.get(this.storeName, key);
  }

  public async put(item: StoreValue<S, StoreNames<S>>) {
    const db = await this.dbPromise;
    return db!.put(this.storeName, item);
  }

  public async delete(key: StoreKey<S, StoreNames<S>> | IDBKeyRange) {
    const db = await this.dbPromise;
    return db!.delete(this.storeName, key);
  }

  public async getAll(): Promise<Array<StoreValue<S, StoreNames<S>>>> {
    const db = await this.dbPromise;
    return db!.getAll(this.storeName);
  }

  public async getAllFromIndex(index: IndexNames<S, StoreNames<S>>) {
    const db = await this.dbPromise;
    return db!.getAllFromIndex(this.storeName, index);
  }

  public async getAllByPropertyConditions(
    predicate: (item: StoreValue<S, StoreNames<S>>) => boolean,
  ) {
    const db = await this.dbPromise;
    const tx = db!.transaction(this.storeName);
    const store = tx.objectStore(this.storeName);
    const results: Array<StoreValue<S, StoreNames<S>>> = [];

    let cursor = await store.openCursor();

    while (cursor) {
      if (predicate(cursor.value)) {
        results.push(cursor.value);
      }
      cursor = await cursor.continue();
    }

    return results;
  }

  public async addRange(items: Array<StoreValue<S, StoreNames<S>>>) {
    const db = await this.dbPromise;
    const tx = db!.transaction(this.storeName, 'readwrite');
    const store = tx.objectStore(this.storeName);

    for (const item of items) {
      await store.add(item);
    }

    await tx.done;
  }
}
